import type { AppointmentStatus } from "@planerius/contracts"
import type {
  BusyBlockView,
  CabinetAppointmentView,
  CabinetBusyKind,
  CabinetSlotHoldKind,
  ScheduleEmployeeView,
  ScheduleResult,
} from "@planerius/contracts/cabinet"

import { formatTimeOfDay } from "../lib/format"
import { formatDuration, formatRange, localTimeToMinutes, minutesFromDayStart } from "./journal-time"

/**
 * Разбор ответа сервера в то, что рисует сетка.
 *
 * Модель говорит В МИНУТАХ, а не в пикселях. Это граница ответственности: в минутах
 * живут факты («Иванов с 10:00 до 10:45»), в пикселях — плотность, которая меняется
 * от шага сетки. Смешав их, мы получили бы модель, которую надо пересчитывать целиком
 * при каждом переключении шага, и проверку, в которой невозможно отличить ошибку данных
 * от ошибки округления.
 *
 * Здесь нет React и нет ни одного цвета.
 */

/** Отличительный признак записи. Порядок фиксирован — по нему значки стоят в карточке. */
export type JournalFlag = "online" | "comment" | "prepaid" | "pinned" | "blacklist" | "first"

const FLAG_ORDER: readonly JournalFlag[] = [
  "online",
  "comment",
  "prepaid",
  "pinned",
  "blacklist",
  "first",
]

export const FLAG_TITLES: Readonly<Record<JournalFlag, string>> = {
  online: "Записался онлайн",
  comment: "Есть комментарий",
  prepaid: "Внесена предоплата",
  pinned: "Закреплённая заметка о клиенте",
  blacklist: "Ограничена онлайн-запись",
  first: "Первый визит",
}

export const FLAG_GLYPHS: Readonly<Record<JournalFlag, string>> = {
  online: "●",
  comment: "✎",
  prepaid: "₽",
  pinned: "!",
  blacklist: "⊘",
  first: "1",
}

export interface JournalCard {
  readonly id: string
  readonly status: AppointmentStatus
  readonly startMinute: number
  readonly endMinute: number
  readonly durationMinutes: number
  /**
   * Сколько минут до начала СЛЕДУЮЩЕЙ записи в этой же колонке. Нужно ровно одному
   * правилу — перерастанию короткой карточки: расти можно, закрывать соседа нельзя.
   */
  readonly minutesToNext: number | null
  readonly clientName: string
  readonly serviceName: string
  /** «+ ещё 2» при нескольких услугах в корзине. */
  readonly extraServices: number
  readonly timeLabel: string
  readonly flags: readonly JournalFlag[]
  readonly tooltip: string
  /**
   * Запись посажена в паузу составной услуги соседа — рисуется в правой половине
   * колонки. Единственный штатный случай двух карточек в одно время (ТЗ §2.4.2).
   */
  readonly inPause: boolean
}

export interface JournalPause {
  readonly id: string
  readonly startMinute: number
  readonly endMinute: number
  readonly label: string
}

export interface JournalBusy {
  readonly id: string
  readonly kind: CabinetBusyKind
  readonly startMinute: number
  readonly endMinute: number
  readonly label: string
  /** Буфер и уборка принадлежат соседней записи и читаются как её продолжение. */
  readonly isSystem: boolean
}

export interface JournalHold {
  readonly id: string
  readonly kind: CabinetSlotHoldKind
  readonly startMinute: number
  readonly endMinute: number
  readonly title: string
  readonly expiresAt: string
  readonly isOwn: boolean
  readonly isQuarantine: boolean
}

export interface JournalColumn {
  readonly employee: ScheduleEmployeeView
  /** Рабочее окно мастера. null — сегодня он не работает: это другой ответ, чем «занят». */
  readonly workStartMinute: number | null
  readonly workEndMinute: number | null
  readonly cards: readonly JournalCard[]
  readonly pauses: readonly JournalPause[]
  readonly busy: readonly JournalBusy[]
  readonly holds: readonly JournalHold[]
}

export interface JournalWindow {
  readonly openMinute: number
  readonly closeMinute: number
  readonly isClosed: boolean
  readonly exceptionReason: string | null
  readonly calendarTitle: string | null
}

export interface JournalSummary {
  readonly appointments: number
  readonly pending: number
  readonly freeMinutes: number
  readonly noShow: number
}

export interface JournalDayModel {
  readonly date: string
  readonly timeZone: string
  readonly window: JournalWindow
  readonly columns: readonly JournalColumn[]
  readonly unassigned: readonly JournalCard[]
  readonly summary: JournalSummary
  readonly generatedAt: string
  /** Мастеров нет вовсе — салон ещё не настроен. Это не «нет записей». */
  readonly hasEmployees: boolean
  /** Мастера есть, но сегодня ни у кого нет смены — третий вид пустоты (ТЗ §2.11.2). */
  readonly hasShifts: boolean
}

/** Запасное окно дня, когда часы филиала не пришли. То же, что в макете: 9:00–21:00. */
const FALLBACK_OPEN = 9 * 60
const FALLBACK_CLOSE = 21 * 60

/** Занятость этих видов уже нарисована карточкой записи или занятием — второй раз не надо. */
const BUSY_KINDS_DRAWN_ELSEWHERE: ReadonlySet<CabinetBusyKind> = new Set([
  "service",
  "group_session",
])

const BUSY_LABELS: Readonly<Record<CabinetBusyKind, string>> = {
  service: "Услуга",
  buffer: "Буфер",
  cleanup: "Уборка кресла",
  break: "Перерыв",
  timeoff: "Отсутствие",
  blocked: "Заблокировано",
  group_session: "Групповое занятие",
}

const HOLD_TITLES: Readonly<Record<CabinetSlotHoldKind, string>> = {
  // Прямая формулировка того, что происходит прямо сейчас (ТЗ §2.4.1): человек на витрине
  // выбрал это время и ждёт код из SMS. «Занято» здесь было бы неправдой.
  checkout: "Клиент оформляет запись",
  payment: "Клиент оплачивает запись",
  reschedule: "Клиент переносит запись",
  waitlist: "Место из листа ожидания",
  manual: "Время придержано вручную",
  quarantine: "Время освободилось",
}

/**
 * Что ответить на нажатие по удержанию (2-2.4.1).
 *
 * ПОЧЕМУ ЭТО ВООБЩЕ ЕСТЬ. Удержание — единственный объект сетки, который нельзя ни
 * открыть, ни занять, и молчаливое нажатие по нему администратор читает как «интерфейс
 * сломался». Макет отвечает словами: кто держит, до какого времени и что с этим делать.
 *
 * ДЕЙСТВИЕ НАЗВАНО ЗДЕСЬ, А НЕ В РАЗМЕТКЕ, потому что оно зависит от тех же трёх
 * признаков, что и текст: снять можно только СВОЁ, обновить день имеет смысл только
 * у ИСТЁКШЕГО, а карантин занимается записью и объяснять его нажатием незачем.
 */
export interface HoldExplanation {
  readonly title: string
  readonly text: string
  /** «снять» — только для своего, «обновить» — для истёкшего. null — сказать и всё. */
  readonly action: "release" | "refresh" | null
}

export function holdExplanation(input: {
  readonly hold: JournalHold
  readonly timeZone: string
  readonly nowMs: number
  /**
   * Срок удержания из правил салона: сколько слот держался, прежде чем был отпущен.
   * null — правила ещё не загружены, и число не выдумывается: «держался 5 минут»
   * при настройке салона в 15 минут — это выдумка, которую человек примет за факт.
   */
  readonly holdTtlSeconds: number | null
}): HoldExplanation {
  const { hold } = input
  const freeAt = formatTimeOfDay(hold.expiresAt, input.timeZone)

  // ИСТЁКШЕЕ УДЕРЖАНИЕ — ПЕРВЫМ ДЕЛОМ. Оно выглядит как живое (строка в сетке никуда
  // не делась до перезагрузки дня), а обещанием слота уже не является: предлагать
  // «снять» то, чего нет, — способ получить отказ сервера на пустом месте.
  if (Date.parse(hold.expiresAt) <= input.nowMs) {
    return {
      title: "Удержание истекло",
      text:
        input.holdTtlSeconds === null
          ? "Время больше не удерживается. Проверьте, свободен ли он ещё."
          : `Время больше не удерживается. Слот держался ${formatDuration(Math.round(input.holdTtlSeconds / 60))} и был отпущен. Проверьте, свободен ли он ещё.`,
      action: "refresh",
    }
  }

  if (hold.isQuarantine) {
    return {
      title: "Время освободилось",
      text: `В онлайн-запись вернётся в ${freeAt} — до этого его можно занять вручную.`,
      action: null,
    }
  }

  if (hold.isOwn) {
    return {
      title: "Держите вы",
      text: `Вы придержали это время. Оно освободится в ${freeAt}, если не записать клиента.`,
      action: "release",
    }
  }

  // Удержание, поставленное рукой ДРУГОГО сотрудника, — не «клиент на сайте»: сказать
  // про витрину там, где коллега держит слот для своего звонящего, значит отправить
  // администратора искать несуществующего человека в онлайн-записи.
  if (hold.kind === "manual") {
    return {
      title: HOLD_TITLES.manual,
      text: `Это время придержал другой сотрудник. Освободится в ${freeAt}, если он не запишет клиента.`,
      action: null,
    }
  }

  return {
    title: HOLD_TITLES[hold.kind],
    text: `Это время сейчас удерживает клиент, который оформляет запись на сайте. Освободится в ${freeAt}, если он не завершит запись.`,
    action: null,
  }
}

function minutes(instant: string, timeZone: string, date: string): number {
  return minutesFromDayStart(instant, timeZone, date)
}

/** Признаки записи. Каждый — ответ на вопрос, который администратор задаёт взглядом. */
function flagsOf(appointment: CabinetAppointmentView): readonly JournalFlag[] {
  const present = new Set<JournalFlag>()
  // Источник ≠ admin и ≠ api: запись завёл сам клиент, а не сотрудник и не интеграция.
  if (appointment.source !== "admin" && appointment.source !== "api") present.add("online")
  if (appointment.comment !== null && appointment.comment.trim() !== "") present.add("comment")
  if (appointment.prepaidMinor !== null && appointment.prepaidMinor > 0) present.add("prepaid")
  if (appointment.client.pinnedNotes.length > 0) present.add("pinned")
  if (appointment.client.isBlacklisted) present.add("blacklist")
  if (appointment.client.visitsCount === 0) present.add("first")
  return FLAG_ORDER.filter((flag) => present.has(flag))
}

function toCard(
  appointment: CabinetAppointmentView,
  timeZone: string,
  date: string,
  inPause: boolean,
): JournalCard {
  const startMinute = minutes(appointment.startAt, timeZone, date)
  const endMinute = minutes(appointment.endAt, timeZone, date)
  const first = appointment.services[0]
  const serviceName = first?.name ?? "Услуга"
  const extraServices = Math.max(appointment.services.length - 1, 0)
  const timeLabel = formatRange(startMinute, endMinute)
  const more = extraServices > 0 ? ` + ещё ${extraServices}` : ""

  return {
    id: appointment.id,
    status: appointment.status,
    startMinute,
    endMinute,
    durationMinutes: Math.max(endMinute - startMinute, 0),
    minutesToNext: null,
    clientName: appointment.client.displayName,
    serviceName,
    extraServices,
    timeLabel,
    flags: flagsOf(appointment),
    // Подсказка нужна карточке высотой 20 px, в которой видно только имя: навёл — прочитал
    // всё. Она НЕ единственный способ узнать это (на телефоне наведения нет), но здесь
    // она дешевле любого другого.
    tooltip: `${appointment.client.displayName} · ${timeLabel} · ${serviceName}${more}`,
    inPause,
  }
}

/** Паузы составной услуги: шаг не занимает мастера, но занимает кресло (ТЗ §2.4.2). */
function pausesOf(
  appointments: readonly CabinetAppointmentView[],
  timeZone: string,
  date: string,
): readonly JournalPause[] {
  const result: JournalPause[] = []
  for (const appointment of appointments) {
    for (const step of appointment.steps) {
      if (step.occupiesEmployee || !step.occupiesResource) continue
      const startMinute = minutes(step.startAt, timeZone, date)
      const endMinute = minutes(step.endAt, timeZone, date)
      const duration = Math.max(endMinute - startMinute, 0)
      result.push({
        id: step.id,
        startMinute,
        endMinute,
        // Разрыв обязан читаться как «сюда можно записать другого», а не как ошибка
        // отрисовки — поэтому подпись говорит и про мастера, и про кресло.
        label: `Выдержка ${duration} мин · мастер свободен, кресло занято`,
      })
    }
  }
  return result
}

function busyLabel(block: BusyBlockView): string {
  const base = BUSY_LABELS[block.kind]
  const note = block.note?.trim() ?? ""
  if (block.kind === "blocked") {
    const parts = ["Заблокировано"]
    if (note !== "") parts.push(note)
    if (block.createdByName !== null) parts.push(block.createdByName)
    return parts.join(" · ")
  }
  if (block.kind === "break" || block.kind === "timeoff") return note === "" ? base : note
  return base
}

/**
 * Окно дня.
 *
 * Берётся из часов филиала, но РАСШИРЯЕТСЯ до всего, что в этот день есть. Администратор
 * заводит записи до открытия («клиент придёт пораньше»), а отпуск мастера идёт сутками —
 * и такая запись обязана быть видна, а не обрезана границей часов (ТЗ §2.2.1).
 * Расширение округляется наружу до часа, иначе шкала начинается с 08:47.
 */
/**
 * Границы суток этого дня — то, что считается «принадлежащим дню» ДО учёта записей.
 *
 * Часы филиала и смены дня расширяют сутки наружу, и это не формальность: смена
 * 22:00–06:00 кончается на 1800-й минуте от начала своего дня, и записи с 00:30
 * принадлежат ЕЙ, а не утру следующего дня. Сами записи здесь не участвуют — иначе
 * рассуждение пошло бы по кругу: окно считается по записям, а записи отбираются по окну.
 */
function daySpan(
  result: ScheduleResult,
  date: string,
  timeZone: string,
): { readonly lower: number; readonly upper: number } {
  const day = result.days.find((item) => item.date === date) ?? result.days[0] ?? null
  const open = localTimeToMinutes(day?.opensAt ?? null) ?? FALLBACK_OPEN
  const close = localTimeToMinutes(day?.closesAt ?? null) ?? FALLBACK_CLOSE

  let lower = Math.min(0, open, close)
  let upper = Math.max(24 * 60, open, close)
  for (const workday of result.workdays) {
    if (workday.date !== date) continue
    lower = Math.min(lower, minutes(workday.startAt, timeZone, date))
    upper = Math.max(upper, minutes(workday.endAt, timeZone, date))
  }
  return { lower, upper }
}

/**
 * Ответ журнала, СУЖЕННЫЙ до одного дня.
 *
 * ЗАЧЕМ ЭТО ВООБЩЕ НУЖНО. Дневной экран запрашивает один день, и до появления недели
 * сужать было нечего. Неделя запрашивает семь дней ОДНИМ запросом — и дневной расчёт,
 * которому отдали недельный ответ, считал чужие сутки своими: окно растягивалось
 * записями всех семи дней, шкала уходила в отрицательные часы («-132:00»), сетка
 * получала шесть сотен строк, в каждой колонке мастера рисовались записи всей недели,
 * а итог «записей за день» показывал недельное число. Расхождение с дневным экраном
 * возникало ровно там, где пакет обещал его исключить повторным вызовом дневного
 * расчёта: дневной экран был цел только потому, что запрашивал один день.
 *
 * ПРАВИЛО ОТБОРА — ПЕРЕСЕЧЕНИЕ СО СУТКАМИ ДНЯ, А НЕ РАВЕНСТВО ДАТЫ. Равенство отобрало
 * бы лишнее: запись 21:30 в филиале, закрывающемся в 21:00, обязана быть видна
 * (ТЗ §2.2.1), а запись с 00:30 при ночной смене принадлежит вечеру предыдущего дня.
 * Поэтому оставляем то, что пересекается с сутками, расширенными сменами дня.
 */
export function narrowToDate(result: ScheduleResult, date: string): ScheduleResult {
  const timeZone = result.branch.timezone
  const { lower, upper } = daySpan(result, date, timeZone)
  const keep = (startAt: string, endAt: string): boolean =>
    minutes(endAt, timeZone, date) > lower && minutes(startAt, timeZone, date) < upper

  return {
    ...result,
    appointments: result.appointments.filter((item) => keep(item.startAt, item.endAt)),
    busy: result.busy.filter((item) => keep(item.startAt, item.endAt)),
    holds: result.holds.filter((item) => keep(item.startAt, item.endAt)),
    workdays: result.workdays.filter((item) => item.date === date),
  }
}

function windowOf(result: ScheduleResult, date: string, timeZone: string): JournalWindow {
  const day = result.days.find((item) => item.date === date) ?? result.days[0] ?? null
  const open = localTimeToMinutes(day?.opensAt ?? null) ?? FALLBACK_OPEN
  const close = localTimeToMinutes(day?.closesAt ?? null) ?? FALLBACK_CLOSE

  let openMinute = Math.min(open, close)
  let closeMinute = Math.max(open, close)

  const stretch = (startAt: string, endAt: string): void => {
    const from = minutes(startAt, timeZone, date)
    const to = minutes(endAt, timeZone, date)
    if (from < openMinute) openMinute = Math.floor(from / 60) * 60
    if (to > closeMinute) closeMinute = Math.ceil(to / 60) * 60
  }

  for (const appointment of result.appointments) stretch(appointment.startAt, appointment.endAt)
  for (const block of result.busy) stretch(block.startAt, block.endAt)
  for (const hold of result.holds) stretch(hold.startAt, hold.endAt)
  for (const workday of result.workdays) {
    if (workday.date === date) stretch(workday.startAt, workday.endAt)
  }

  // Вырожденный день (часы совпали или пришли пустыми) всё равно должен дать сетку,
  // в которой видно хотя бы час: пустой контейнер нулевой высоты выглядит как поломка.
  if (closeMinute - openMinute < 60) closeMinute = openMinute + 60

  return {
    openMinute,
    closeMinute,
    isClosed: day?.isClosed ?? false,
    exceptionReason: day?.exceptionReason ?? null,
    calendarTitle: day?.calendarHint?.title ?? null,
  }
}

/** Попадает ли запись целиком внутрь паузы соседней записи. */
function insideAnyPause(
  card: { readonly startMinute: number; readonly endMinute: number; readonly id: string },
  pauses: readonly JournalPause[],
): boolean {
  return pauses.some(
    (pause) => card.startMinute >= pause.startMinute && card.endMinute <= pause.endMinute,
  )
}

/**
 * Порядок колонок.
 *
 * В контракте нет поля порядка, а ТЗ §2.2.1 говорит: если порядок не задан — по имени.
 * Поэтому сортировка по displayName, и она устойчива: журнал, переставляющий колонки
 * между перелистываниями дней, ломает мышечную память администратора надёжнее
 * любой ошибки в данных.
 */
function sortEmployees(employees: readonly ScheduleEmployeeView[]): ScheduleEmployeeView[] {
  return employees.toSorted((a, b) => a.displayName.localeCompare(b.displayName, "ru"))
}

export function buildJournalDay(
  rawResult: ScheduleResult,
  date: string,
  options: { readonly includeCancelled: boolean },
): JournalDayModel {
  /*
   * СУЖЕНИЕ ДО ДНЯ — ПЕРВОЕ ДЕЙСТВИЕ, И ОНО ОБЯЗАТЕЛЬНО. Ответ может быть недельным
   * (режим «Неделя» запрашивает семь дней одним запросом), и без сужения каждый день
   * считал бы своими записи всех остальных: окно, колонки, сводка и «свободно» — всё
   * посчиталось бы по неделе, а подписано было бы одним днём.
   */
  const result = narrowToDate(rawResult, date)
  const timeZone = result.branch.timezone
  const window = windowOf(result, date, timeZone)

  const visible = result.appointments.filter(
    (appointment) => options.includeCancelled || appointment.status !== "cancelled",
  )

  const columns = sortEmployees(result.employees).map((employee): JournalColumn => {
    const workday = result.workdays.find(
      (item) => item.employeeId === employee.id && item.date === date,
    )
    const mine = visible
      .filter((appointment) => appointment.employeeId === employee.id)
      .toSorted((a, b) => a.startAt.localeCompare(b.startAt))

    const pauses = pausesOf(mine, timeZone, date)
    const raw = mine.map((appointment) => toCard(appointment, timeZone, date, false))
    // Запись, посаженная в паузу соседа, уходит в правую половину колонки и НЕ участвует
    // в расчёте «следующей записи»: иначе соседняя карточка решит, что её место занято.
    const inPauseIds = new Set(
      raw.filter((card) => insideAnyPause(card, pauses)).map((card) => card.id),
    )
    const mainLine = raw.filter((card) => !inPauseIds.has(card.id))

    const cards = [
      ...mainLine.map((card, index): JournalCard => {
        const next = mainLine[index + 1]
        return {
          ...card,
          minutesToNext: next === undefined ? null : next.startMinute - card.startMinute,
        }
      }),
      ...raw
        .filter((card) => inPauseIds.has(card.id))
        .map((card): JournalCard => ({ ...card, inPause: true })),
    ]

    const busy = result.busy
      .filter(
        (block) =>
          block.employeeId === employee.id && !BUSY_KINDS_DRAWN_ELSEWHERE.has(block.kind),
      )
      .map(
        (block): JournalBusy => ({
          id: block.id,
          kind: block.kind,
          startMinute: minutes(block.startAt, timeZone, date),
          endMinute: minutes(block.endAt, timeZone, date),
          label: busyLabel(block),
          isSystem: block.isSystem,
        }),
      )

    const holds = result.holds
      .filter((hold) => hold.employeeId === employee.id)
      .map(
        (hold): JournalHold => ({
          id: hold.id,
          kind: hold.kind,
          startMinute: minutes(hold.startAt, timeZone, date),
          endMinute: minutes(hold.endAt, timeZone, date),
          title: HOLD_TITLES[hold.kind],
          expiresAt: hold.expiresAt,
          isOwn: hold.isOwn,
          isQuarantine: hold.kind === "quarantine",
        }),
      )

    return {
      employee,
      workStartMinute:
        workday === undefined ? null : minutes(workday.startAt, timeZone, date),
      workEndMinute: workday === undefined ? null : minutes(workday.endAt, timeZone, date),
      cards,
      pauses,
      busy,
      holds,
    }
  })

  const unassigned = visible
    .filter((appointment) => appointment.employeeId === null)
    .toSorted((a, b) => a.startAt.localeCompare(b.startAt))
    .map((appointment) => toCard(appointment, timeZone, date, false))

  const live = visible.filter((appointment) => appointment.status !== "cancelled")
  const workMinutes = columns.reduce((sum, column) => {
    if (column.workStartMinute === null || column.workEndMinute === null) return sum
    return sum + Math.max(column.workEndMinute - column.workStartMinute, 0)
  }, 0)
  const bookedMinutes = live.reduce(
    (sum, appointment) =>
      sum +
      Math.max(
        minutes(appointment.endAt, timeZone, date) - minutes(appointment.startAt, timeZone, date),
        0,
      ),
    0,
  )

  return {
    date,
    timeZone,
    window,
    columns,
    unassigned,
    summary: {
      appointments: live.length,
      pending: live.filter((appointment) => appointment.status === "pending").length,
      freeMinutes: Math.max(workMinutes - bookedMinutes, 0),
      noShow: live.filter((appointment) => appointment.status === "no_show").length,
    },
    generatedAt: result.generatedAt,
    hasEmployees: columns.length > 0,
    hasShifts: columns.some((column) => column.workStartMinute !== null),
  }
}
